/**
 * The accessory tests (snippets/sunday-demo.liquid): tap the drawing or its button to play, tap again to play again.
 * protector: the chai pours, lands, the corner lifts: dry underneath (states s0 → s1 → s2).
 * pillow: night 1 → 365; the ordinary pillow sinks, the Delight stays level (s0 → s1 → s2).
 * topper: the topper slides on and off (class on).
 * Words: the "demo" group of the language file (sunday.js.demo), printed by snippets/sunday-js-text.liquid.
 * A stage can be put back to its start with stage.sdReset() (the collection's tabs do this).
 */
import { t } from '@sunday/text';
import { calm } from '@sunday/util';

const T = (k) => t('demo.' + k);
const NB = ' ';

function say(z, head, sub) {
  const r = z.querySelector('[data-dres]');
  if (!r) return;
  r.textContent = head || NB;
  const s = document.createElement('small');
  s.textContent = sub || NB;
  r.append(s);
}
const btn = (z) => z.querySelector('[data-demo-go]');
/* 24 Oct (AR go): the round ▶ button says what it does to screen readers; its ring fills while the test plays */
const lab = (b, s) => b && b.setAttribute('aria-label', s);
function ring(z, ms) {
  const b = btn(z);
  if (!b) return;
  b.style.setProperty('--sd-ring-dur', ms + 'ms');
  b.classList.remove('sd-ring-on');
  void b.offsetWidth;
  b.classList.add('sd-ring-on');
  clearTimeout(b.__r);
  b.__r = setTimeout(() => b.classList.remove('sd-ring-on'), ms + 60);
}
const swap = (z, from, to) => {
  z.classList.remove(...from);
  void z.offsetWidth; // restart the transitions
  z.classList.add(to);
};

/* ---------- protector ---------- */
function protector(z) {
  const st = Number(z.dataset.st || 0);
  if (st === 1) return;
  const pour = () => {
    z.dataset.st = '1';
    ring(z, 1800);
    swap(z, ['sd-s0', 'sd-s2'], 'sd-s1');
    say(z, T('prot_s1'), NB);
    clearTimeout(z.__t);
    z.__t = setTimeout(() => {
      z.dataset.st = '2';
      z.classList.remove('sd-s1');
      z.classList.add('sd-s2');
      say(z, T('prot_s2'), T('prot_s2b'));
      lab(btn(z), T('prot_again'));
    }, 1800);
  };
  if (st === 2) {
    swap(z, ['sd-s2'], 'sd-s0');
    z.__t = setTimeout(pour, 350);
  } else pour();
}

/* ---------- pillow ---------- */
/* 21 Oct (AR): a calendar flips Jan → Dec (about 3.6 s) while the ordinary pillow sinks; the Delight stays level. */
function pillow(z) {
  const st = Number(z.dataset.st || 0);
  const cal = z.querySelector('[data-dcal]');
  const dm = z.querySelector('[data-dm]');
  const months = cal ? (cal.dataset.months || '').split('|') : [];
  const b = btn(z);
  const show = (i) => {
    if (!dm || !months[i]) return;
    dm.textContent = months[i];
    cal.classList.remove('sd-flip');
    void cal.offsetWidth;
    cal.classList.add('sd-flip');
  };
  if (st === 1) return;
  clearTimeout(z.__t);
  clearInterval(z.__i);
  if (st === 2) {
    z.dataset.st = '0';
    swap(z, ['sd-s2'], 'sd-s0');
    if (dm && months[0]) dm.textContent = months[0];
    say(z, T('pil_s0'), T('pil_s0b'));
    lab(b, T('pil_go'));
    return;
  }
  z.dataset.st = '1';
  swap(z, ['sd-s0'], 'sd-s1');
  say(z, NB, NB);
  const n = months.length || 12;
  const step = calm() ? 0 : 300;
  ring(z, step ? step * (n - 1) + 250 : 300);
  let i = 0;
  if (step) {
    z.__i = setInterval(() => {
      i += 1;
      show(Math.min(i, n - 1));
      if (i >= n - 1) clearInterval(z.__i);
    }, step);
  } else show(n - 1);
  z.__t = setTimeout(() => {
    z.dataset.st = '2';
    z.classList.remove('sd-s1');
    z.classList.add('sd-s2');
    show(n - 1);
    say(z, T('pil_s2'), T('pil_s2b'));
    lab(b, T('pil_back'));
  }, step ? step * (n - 1) + 250 : 0);
}

/* ---------- topper ---------- */
function topper(z) {
  const b = btn(z);
  if (b.disabled) return;
  const on = !z.classList.contains('sd-on');
  z.classList.toggle('sd-on', on);
  b.disabled = true;
  ring(z, 1150);
  const r = z.querySelector('[data-dres]');
  r.style.opacity = '0';
  clearTimeout(z.__t);
  z.__t = setTimeout(() => {
    if (on) say(z, T('top_s1'), T('top_s1b'));
    else say(z, T('top_s0'), T('top_s0b'));
    r.style.opacity = '1';
    lab(b, on ? T('top_off') : T('top_go'));
    b.disabled = false;
  }, 1150);
}

const PLAY = { protector, pillow, topper };

function reset(z) {
  clearTimeout(z.__t);
  const k = z.dataset.demo;
  const b = btn(z);
  z.dataset.st = '0';
  b.disabled = false;
  if (k === 'topper') {
    z.classList.remove('sd-on');
    z.querySelector('[data-dres]').style.opacity = '1';
    say(z, T('top_s0'), T('top_s0b'));
    lab(b, T('top_go'));
    return;
  }
  z.classList.remove('sd-s1', 'sd-s2');
  z.classList.add('sd-s0');
  if (k === 'protector') {
    say(z, T('prot_s0'), T('prot_s0b'));
    lab(b, T('prot_go'));
  } else {
    clearInterval(z.__i);
    const cal = z.querySelector('[data-dcal]');
    const dm = z.querySelector('[data-dm]');
    if (cal && dm) dm.textContent = (cal.dataset.months || '').split('|')[0] || dm.textContent;
    say(z, T('pil_s0'), T('pil_s0b'));
    lab(b, T('pil_go'));
  }
}

for (const z of document.querySelectorAll('[data-demo]')) z.sdReset = () => reset(z);

document.addEventListener('click', (e) => {
  const go = e.target.closest('[data-demo-go]');
  /* 24 Oct (AR): a tap anywhere on the test plays it (links and other controls keep their own job) */
  if (!go && e.target.closest('a, button, input, select, label, [data-demo-uses]')) return;
  const art = !go && e.target.closest('[data-demo]');
  if (!go && !art) return;
  const z = go ? go.closest('[data-demo]') : art;
  if (!z) return;
  if (!z.sdReset) z.sdReset = () => reset(z);
  if (art && btn(z).disabled) return;
  const f = PLAY[z.dataset.demo];
  if (f) f(z);
});
